/**
 * Contextual “what to do next” for requisition detail right rail (benchmark UX).
 */
import type { Requisition } from '@core/types';
import type { RequisitionStatus } from '@core/enums';
import { REQUISITION_STATUS_LABELS } from './requisitionLifecycle';

export type RequisitionContextAction = {
  id: string;
  title: string;
  detail: string;
  /** Primary = strongest CTA */
  priority: 'primary' | 'secondary' | 'neutral';
  href?: string;
};

function baseStatus(req: Requisition): RequisitionStatus {
  return req.status;
}

export function getRequisitionStatusHeadline(req: Requisition): string {
  return REQUISITION_STATUS_LABELS[baseStatus(req)] ?? req.status;
}

export function getRequisitionContextActions(req: Requisition): RequisitionContextAction[] {
  const s = baseStatus(req);
  const linked = Boolean(req.job_id ?? req.created_job_id);
  const intakeStep = req.intake_wizard_step;

  if (s === 'draft' && intakeStep != null) {
    return [
      {
        id: 'finish-intake',
        title: 'Finish intake wizard',
        detail: `You left off on step ${intakeStep} of 5. Complete the guided form to finalize this draft.`,
        priority: 'primary',
        href: `/requisitions/${req.id}/intake`,
      },
      {
        id: 'overview',
        title: 'Or use Overview tabs',
        detail: 'Edit fields from the Details tab if you prefer the classic layout.',
        priority: 'neutral',
      },
    ];
  }

  if (s === 'draft') {
    return [
      {
        id: 'client-gate',
        title: 'Email the hiring client',
        detail: 'Use Hiring client gate on Overview to send acknowledgement and move to Awaiting client.',
        priority: 'primary',
      },
      {
        id: 'skip-gate',
        title: 'Client already confirmed?',
        detail: 'Use “Skip client email” only when acknowledgement happened outside the product.',
        priority: 'secondary',
      },
    ];
  }

  if (s === 'awaiting_client') {
    return [
      {
        id: 'log-reply',
        title: 'Log client replies',
        detail: 'Paste or summarise emails so the team has an audit trail.',
        priority: 'secondary',
      },
      {
        id: 'confirm',
        title: 'Record client confirmation',
        detail: 'When the employer confirms in writing, record it to start internal approval.',
        priority: 'primary',
      },
      {
        id: 'resend',
        title: 'Resend outreach',
        detail: 'Queue another email if the client needs a nudge.',
        priority: 'neutral',
      },
    ];
  }

  if (s === 'submitted') {
    return [
      {
        id: 'approve-job',
        title: 'Approve & create job',
        detail: 'Fast path: approve and auto-create a draft job from this requisition.',
        priority: 'primary',
      },
      {
        id: 'approve-only',
        title: 'Approve only',
        detail: 'Approve the mandate without creating a job yet.',
        priority: 'secondary',
      },
      {
        id: 'reject',
        title: 'Reject or send back',
        detail: 'Reject with a note, or send back to draft for changes.',
        priority: 'neutral',
      },
    ];
  }

  if (s === 'approved') {
    return [
      {
        id: 'create-job',
        title: linked ? 'Open linked job' : 'Create job from requisition',
        detail: linked
          ? 'Continue in Jobs to publish and syndicate when ready.'
          : 'Publish a draft JD tied to this client and role.',
        priority: 'primary',
      },
      {
        id: 'sourcing',
        title: 'Mark sourcing',
        detail: 'If recruiting starts before a JD is ready, move to Sourcing from lifecycle.',
        priority: 'secondary',
      },
    ];
  }

  if (s === 'sourcing') {
    return [
      {
        id: 'pipeline',
        title: 'Run the pipeline',
        detail: 'Work candidates in Applications; keep the linked job status in sync.',
        priority: 'primary',
      },
      {
        id: 'filled',
        title: 'Mark filled',
        detail: 'When the hire is confirmed, close the requisition as Filled.',
        priority: 'secondary',
      },
      {
        id: 'hold',
        title: 'Pause if needed',
        detail: 'Place on hold when the client pauses the search.',
        priority: 'neutral',
      },
    ];
  }

  if (s === 'filled') {
    return [
      {
        id: 'done',
        title: 'Mandate complete',
        detail: 'Archive reporting and revisit linked jobs if you need post-hire updates.',
        priority: 'neutral',
      },
    ];
  }

  if (s === 'on_hold') {
    return [
      {
        id: 'resume',
        title: 'Resume sourcing',
        detail: 'When the client is ready, resume from the lifecycle panel.',
        priority: 'primary',
      },
    ];
  }

  if (s === 'rejected') {
    return [
      {
        id: 'reopen',
        title: 'Reopen as draft',
        detail: 'Adjust the brief and run the client gate again.',
        priority: 'primary',
      },
      {
        id: 'resubmit',
        title: 'Resubmit for approval',
        detail: 'If only internal sign-off was blocking, resubmit after fixes.',
        priority: 'secondary',
      },
    ];
  }

  if (s === 'cancelled') {
    return [
      {
        id: 'reopen-cancel',
        title: 'Reopen as draft',
        detail: 'Bring the mandate back if the client re-engages.',
        priority: 'secondary',
      },
    ];
  }

  return [];
}
